import { useOnboarding } from "@/context/OnboardingContext";
import {
  OnboardingConcernOptionEnum,
  OnboardingStepEnum,
} from "@/domain/onboarding";
import { useOnboardingEntryPointData } from "@/hooks/useOnboardingEntryPointData";
import {
  getConcernOptionSubtextI18nPath,
  OnboardingI18nPathType,
  OnboardingTextType,
} from "@/utils/onboarding";

export type OnboardingNextStepsSectionType = {
  number: number;
  title: OnboardingTextType;
  description: OnboardingTextType;
  duration: OnboardingTextType;
  isDurationHidden: boolean;
};

// The next steps step's copy for the current entry point, variant and the
// concern picked earlier, independent of how a layout presents it.
export const useOnboardingNextStepsContent = () => {
  const { getI18nKey, selectedConcernOption } = useOnboarding();
  const { isAwarenessCampaign, i18nKeyContext, isMedicaidActive } =
    useOnboardingEntryPointData(OnboardingStepEnum.NEXT_STEPS);

  // The Part A and B and the Part A, B and D concerns have their own next steps
  // copy within the same entry point.
  const isPartAOrBOrDOptionSelected =
    selectedConcernOption === OnboardingConcernOptionEnum.PART_A_AND_B ||
    selectedConcernOption === OnboardingConcernOptionEnum.PART_A_B_AND_D;

  const parsedContext = isPartAOrBOrDOptionSelected
    ? `${i18nKeyContext}_partABorD`
    : i18nKeyContext;

  const toText = (path: OnboardingI18nPathType): OnboardingTextType => ({
    i18nKey: getI18nKey(path),
    i18nOptions: { context: parsedContext },
  });

  const sections: OnboardingNextStepsSectionType[] = [
    {
      number: 1,
      title: toText("nextSteps.step1Title"),
      description: toText("nextSteps.step1Description"),
      duration: toText("nextSteps.step1Duration"),
      isDurationHidden: false,
    },
    {
      number: 2,
      title: toText("nextSteps.step2Title"),
      description: toText("nextSteps.step2Description"),
      duration: toText("nextSteps.step2Duration"),
      isDurationHidden: false,
    },
    {
      number: 3,
      title: toText("nextSteps.step3Title"),
      description: toText("nextSteps.step3Description"),
      duration: toText("nextSteps.step3Duration"),
      isDurationHidden: isAwarenessCampaign || isMedicaidActive,
    },
  ];

  return {
    title: toText("nextSteps.title"),
    description: toText(getConcernOptionSubtextI18nPath(selectedConcernOption)),
    sections,
  };
};
